import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight, Phone } from "lucide-react";
import { MaskLines } from "@/components/site/Reveal";
import { CONTACT } from "@/data/products";

const RotatingBadge = () => (
  <div className="spin-slow absolute -left-10 -top-10 z-20 h-28 w-28 md:-left-14 md:-top-14 md:h-36 md:w-36">
    <svg viewBox="0 0 100 100" className="h-full w-full">
      <defs><path id="c" d="M50 50m-38 0a38 38 0 1 1 76 0a38 38 0 1 1-76 0" /></defs>
      <circle cx="50" cy="50" r="50" fill="#FFC400" />
      <text className="font-mono" fontSize="8.4" letterSpacing="2" fill="#0B0B0B">
        <textPath href="#c">MOQ 100 KG • EST. 1997 • KANPUR • </textPath>
      </text>
      <circle cx="50" cy="50" r="6" fill="#FF5A00" />
    </svg>
  </div>
);

const HeroVisual = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={reset} className="relative [perspective:1200px]" data-testid="hero-visual">
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative [transform-style:preserve-3d]"
        initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }}
        transition={{ duration: 1.4, ease: [0.76, 0, 0.24, 1], delay: 0.35 }}>
        <div className="relative aspect-[4/5] overflow-hidden rounded-t-full bg-white">
          <motion.img style={{ y, scale: 1.15 }} src="/img/hero-ink.jpg" alt="Orange, yellow and black printing ink swirl" className="h-full w-full object-cover" />
        </div>
      </motion.div>
      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ duration: 0.8, delay: 1.3, ease: [0.22, 1, 0.36, 1] }}>
        <RotatingBadge />
      </motion.div>
      <p className="mt-4 flex justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-600">
        <span>Fig. 01 — Pigment in motion</span><span>TI/26</span>
      </p>
    </div>
  );
};

export const Hero = () => (
  <section data-testid="hero-section" className="relative overflow-hidden pt-[72px]">
    <div className="mx-auto grid max-w-[1440px] gap-12 px-5 pb-16 pt-12 md:grid-cols-12 md:px-10 md:pb-24 md:pt-20">
      <div className="flex flex-col justify-between md:col-span-7">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}
          className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-zinc-700">
          <span className="h-2 w-2 bg-[#FF5A00]" /> Tide Inchem Pvt. Ltd. — Kanpur, India
        </motion.p>
        <h1 data-testid="hero-title" className="font-wide mt-10 text-[13vw] font-black uppercase leading-[0.86] tracking-[-0.03em] md:text-[7.4vw] xl:text-[112px]">
          <MaskLines delay={0.15} lines={[
            "Ink that",
            <span key="b">holds its <span className="text-[#FF5A00]">colour</span></span>,
            "on every",
            <span key="d" className="whitespace-nowrap">bag. <span className="ml-[0.1em] inline-block h-[0.62em] w-[0.62em] rounded-full bg-[#FFC400]" /></span>,
          ]} />
        </h1>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.9 }} className="mt-12 max-w-xl">
          <p className="text-base leading-relaxed text-zinc-700 md:text-lg">
            Gravure, flexo, lamination and specialty packaging inks — made in Kanpur since 1997 for printers who need the same shade, batch after batch.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/products" data-testid="hero-catalogue-btn" className="group flex items-center gap-3 bg-[#0B0B0B] px-6 py-4 font-mono text-xs uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-[#FF5A00]">
              Explore catalogue <ArrowDownRight className="h-4 w-4 transition-transform duration-500 group-hover:-rotate-90" />
            </Link>
            <a href={`tel:${CONTACT.tel}`} data-testid="hero-call-btn" className="flex items-center gap-3 border border-[#0B0B0B] px-6 py-4 font-mono text-xs uppercase tracking-[0.18em] transition-colors duration-300 hover:bg-[#FFC400]">
              <Phone className="h-4 w-4" /> {CONTACT.phone}
            </a>
          </div>
        </motion.div>
      </div>
      <div className="md:col-span-5 md:pl-6 md:pt-10">
        <div className="mx-auto max-w-[380px] px-8 md:max-w-none md:px-0"><HeroVisual /></div>
      </div>
    </div>
  </section>
);
